import React, { useState, useEffect, useCallback, useMemo } from 'react';
import moment from "moment";
import { Form, Popup } from 'semantic-ui-react';
import { Reports, Rates } from '../../../api/services/next';
import { useCurrentHotel } from '../../../hooks/useCurrentHotel';
import { useMessageResources, useTextResources } from '../../../language';
import GridSearchTemplate from '../../../pages/GridSearchTemplate';
import { ChainAddonsDropDown } from '../../shared/ChainAddonsDropDown';
import CheckBoxToggle from '../../shared/CheckBoxToggle';
import ToggleThreeOptions from '../../shared/ToggleThreeOptions';
import CheckboxWrapper from '../../shared/Checkbox';
import { DatePicker } from '../../shared/DatePicker';
import HotelsDropDown from '../../shared/HotelsDropDown';
import RatesDropDown from '../../shared/RatesDropDown';
import Weekdays from '../../shared/Weekdays';
import { weekDays } from '../../shared/constants';
import { onWarning } from '../../shared/messages';
import {
  formatValueAsCurrency,
  makeIntToDaysOfWeekArray,
  formatMomentDateRange,
  formatDate,
} from '../../shared/utils';
import { endpoint } from "../../../api/http"

const AddonsFinalPrices = () => {
  const currentHotel = useCurrentHotel();
  const [search, setSearch] = useState({});
  const [rates, setRates] = useState([]);

  const text = useTextResources();
  const msg = useMessageResources();

  const fromDate = search['FromToDates[0]'];
  const toDate = search['FromToDates[1]']
  useEffect(() => {
    if (search.HotelId && fromDate && toDate) {
      const spec = {
        HotelId: search.HotelId,
        FromDate: fromDate,
        ToDate: toDate,
        InheritedRates: true,
        GetAllInheritingRates: true,
      };
      Rates.GetRates(spec).then(setRates);
    } else {
      setRates([]);
    }
  }, [fromDate, search.HotelId, toDate]);

  const getDatesRangeCellValue = useCallback((row, columnName) => {
    const range = row[columnName];
    return range ? moment.range(moment(range[0]), moment(range[1])) : null;
  }, []);

  const DatesRangeCell = useCallback(({ value }) => {
    return value ? formatMomentDateRange(value) : "-";
  }, []);

  const datesRangeComparer = useCallback((rangeA, rangeB) => {
    return rangeA.start.valueOf() - rangeB.start.valueOf();
  }, []);

  const getRateNameCellValue = useCallback((row) => {
    return row.RateId && row.RateName ? {
      RateId: row.RateId,
      RateName: row.RateName,
    } : null;
  }, []);

  const RateNameCell = useCallback(({ value }) => {
    if (!value) {
      return '-';
    }

    return (
      <a
        target='_blank'
        rel='noopener noreferrer'
        href={`${endpoint}PMS/pricelist/Edit?RateId=${value.RateId}`}
      >
        {value.RateName}
      </a>
    );
  }, []);

  const rateNameGroupCriteria = useCallback((value) => {
    return { key: value?.RateId ?? '-', value };
  }, []);

  const getHiddenRateNameForExcelCellValue = useCallback((row) => {
    return row.RateName ? row.RateName : '-';
  }, []);

  const getHiddenStartDateForExcelCellValue = useCallback((row) => {
    const startDate = row?.DatesRange[0]; 
    return  startDate ? formatDate(startDate) : '-';
  }, []);

  const getHiddenEndDateForExcelCellValue = useCallback((row) => {
    const endDate = row?.DatesRange[1]; 
    return  endDate ? formatDate(endDate) : '-';
  }, []);

  const getFormattedValueWithCurrencyCellValue = useCallback((row, columnName) => {
    const value = row[columnName];
    return value !== undefined ? formatValueAsCurrency(value, row.CurrencyId): '-';
  }, []);

  const WeekdaysCell = useCallback(({ value }) => {
    return <Weekdays selectedWeekdaysInt={value} onlyChosenDays />;
  }, []);

  const weekDaysIntToString = useCallback((value) => {
    const arrayOfWeekdays = makeIntToDaysOfWeekArray(value);
    const result = arrayOfWeekdays
      .map((dayIndex) => text(weekDays[dayIndex]))
      .join(', ');
    return result;
  }, [text]);

  const getHiddenWeekdaysForExcelCellValue = useCallback((row) => {
    const value = row.Weekdays;
    return weekDaysIntToString(value);
  }, [weekDaysIntToString]);

  const weekDaysPredicate = useCallback((value, filter) => {
    const weekDaysString = weekDaysIntToString(value);
    return weekDaysString.toLowerCase().includes(filter.value.toLowerCase());
  }, [weekDaysIntToString]);

  const hiddenPackageItemForExcel = useCallback((row) => {
    const value = row.PackageItem;
    return value ? '✓' : '✕';
  }, []);

  const checkBoxFormatter = useCallback(({ value }) => {
    return <CheckBoxToggle disabled readOnly checked={value} />;
  }, []);

  const toolTipFormatter = (value) => {
    return <Popup content={value} trigger={<span>{value}</span>} />
  }

  const customizeFooter = worksheet => {
    const rows = worksheet._rows.filter((value, index) => index !== 1);
    worksheet._rows = [];
    rows.forEach((row) => worksheet.addRow(row.values));
  }

  const columns = useMemo(() => [
    {
      name: 'HiddenRateNameForExcel',
      title: text('RateName'),
      getCellValue: getHiddenRateNameForExcelCellValue,
    },
    {
      name: 'HiddenStartDateForExcel',
      title: text('StartDate'),
      getCellValue: getHiddenStartDateForExcelCellValue,
    },
    {
      name: 'HiddenEndDateForExcel',
      title: text('EndDate'),
      getCellValue: getHiddenEndDateForExcelCellValue,
    },
    {
      name: 'DatesRange',
      title: text('DatesRange'),
      getCellValue: getDatesRangeCellValue,
      formatter: DatesRangeCell,
    },
    {
      name: 'Weekdays',
      title: text('Weekdays'),
      formatter: WeekdaysCell,
    },
    {
      name: 'HiddenWeekdaysForExcel',
      title: text('Weekdays'),
      getCellValue: getHiddenWeekdaysForExcelCellValue,
    },
    {
      name: 'RateName',
      title: text('RateName'),
      getCellValue: getRateNameCellValue,
      formatter: RateNameCell,
    },
    {
      name: 'AddonName',
      title: text('AddonName'),
    },
    {
      name: 'PackageItem',
      title: text('PackageItem'),
      formatter: checkBoxFormatter,
      customFilter: (props) => <ToggleThreeOptions {...props} />,
    },
    {
      name: 'hiddenPackageItemForExcel',
      title: text('PackageItem'),
      getCellValue: hiddenPackageItemForExcel,
    },
    {
      name: 'AdultPrice',
      title: text('AdultPrice'),
      getCellValue: getFormattedValueWithCurrencyCellValue,
    },
    {
      name: 'AdultCost',
      title: text('AdultCost'),
      getCellValue: getFormattedValueWithCurrencyCellValue,
    },
    {
      name: 'AdultAllowance',
      title: text('AdultAllowance'),
      getCellValue: getFormattedValueWithCurrencyCellValue,
    },
    {
      name: 'ChildPrice',
      title: text('ChildPrice'),
      getCellValue: getFormattedValueWithCurrencyCellValue,
    },
    {
      name: 'ChildCost',
      title: text('ChildCost'),
      getCellValue: getFormattedValueWithCurrencyCellValue,
    },
    {
      name: 'ChildAllowance',
      title: text('ChildAllowance'),
      getCellValue: getFormattedValueWithCurrencyCellValue,
    },
    {
      name: 'InfantPrice',
      title: text('InfantPrice'),
      getCellValue: getFormattedValueWithCurrencyCellValue,
    },
    {
      name: 'InfantCost',
      title: text('InfantCost'),
      getCellValue: getFormattedValueWithCurrencyCellValue,
    },
    {
      name: 'InfantAllowance',
      title: text('InfantAllowance'),
      getCellValue: getFormattedValueWithCurrencyCellValue,
    },
    {
      name: 'RoomPrice',
      title: text('RoomPrice'),
      getCellValue: getFormattedValueWithCurrencyCellValue,
    },
    {
      name: 'RoomCost',
      title: text('RoomCost'),
      getCellValue: getFormattedValueWithCurrencyCellValue,
    },
    {
      name: 'RoomAllowance',
      title: text('RoomAllowance'),
      getCellValue: getFormattedValueWithCurrencyCellValue,
    },
  ].map(c => ({...c, exportTitle: c.title, title: toolTipFormatter(c.title)})),
     [text, getDatesRangeCellValue, DatesRangeCell, WeekdaysCell, getHiddenWeekdaysForExcelCellValue, getRateNameCellValue, RateNameCell, getHiddenRateNameForExcelCellValue, checkBoxFormatter, hiddenPackageItemForExcel, getFormattedValueWithCurrencyCellValue]);

  const gridOptions = useMemo(() => ({
    search: {
      title: text('AddonsFinalPrices'),
      fields: [
        <Form.Field
          key='HotelId'
          label={text('Hotel')}
          control={HotelsDropDown}
        />,
        <DatePicker
          clearable={true}
          picktype='range'
          label={text('Dates')}
          key='FromToDates'
        />,
        <Form.Field
          label={text('Addons')}
          control={ChainAddonsDropDown}
          key='AddonIds'
          placeholder={text('All')}
          clearable
          width='4'
          allowSelectAll
          multiple
          search
        />,
        <Form.Field
          label={text('Rates')}
          control={RatesDropDown}
          data={rates}
          key='RateIds'
          clearable
          width='4'
          placeholder={text('All')}
          allowSelectAll
          multiple
          search
        />,
        <CheckboxWrapper label={text('IncludeVAT')} key='PriceIncludesVat' />,
      ],
      api: Reports.AddonFinalPrices,
      initial: {
        ...(currentHotel && { HotelId: currentHotel.Id }),
      },
      searchSpec: {
        MaxResults: 1000,
      },
      validate: (state) => {
        let fromDate = state['FromToDates[0]'];
        let toDate = state['FromToDates[1]'];
        if (!fromDate) onWarning(msg('FromDateIsRequired'));
        if (!toDate) onWarning(msg('ToDateIsRequired'));
        return Boolean(fromDate && toDate);
      },
    },
    grid: {
      columns,
      noVirtualTable: true,
      paging: true,
      customOptions: {
        overrideData: true,
        sort: {
          sorting: [
            { columnName: 'RateName', direction: 'asc' },
            { columnName: 'DatesRange', direction: 'asc' },
          ],
          extensions: [
            { columnName: 'DatesRange', compare: datesRangeComparer },
          ],
        },
        group: {
          grouping: [
            { columnName: 'RateName' },
          ],
          extensions: [
            { columnName: 'RateName', groupingEnabled: false },
          ],
          criteria: [
            { columnName: 'RateName', criteria: rateNameGroupCriteria },
          ],
        },
        hidden: {
          hiddenColumns: [
            { columnName: 'hiddenPackageItemForExcel' },
            { columnName: 'HiddenWeekdaysForExcel' },
            { columnName: 'HiddenStartDateForExcel' },
            { columnName: 'HiddenEndDateForExcel' },
          ],
          //Special Weekdays and PackageItem columns that should only be available as part of the exported Excel and not in the UI
        },
      },
      scrollbarX: true,
      columnExtensions: [
        { columnName: 'DatesRange', width: 180 },
        { columnName: 'Weekdays', width: 250 },
        { columnName: 'RateName', width: 170 },
        { columnName: 'AddonName', width: 170 },
      ],
      export: true,
      excel: {
        customizeFooter: customizeFooter,
        hiddenColumns: [
          { columnName: 'Weekdays' },
          { columnName: 'DatesRange' },
          { columnName: 'PackageItem' },
        ],
      },
      filtering: {
        defaults: [{ columnName: 'PackageItem', value: true }],
        extensions: [
          {
            columnName: 'Weekdays',
            predicate: weekDaysPredicate,
          },
          {
            columnName: 'PackageItem',
            predicate: (value, filter) => {
              if (filter.value === undefined) return true;
              return value === filter.value;
            },
          },
        ],
      },
    },
  }), [columns, currentHotel, datesRangeComparer, msg, rateNameGroupCriteria, rates, text, weekDaysPredicate]);

  return (
    <div style={{'overflowX': 'auto'}}>
      <GridSearchTemplate
        options={gridOptions}
        onSearchChange={setSearch}
      />
    </div>
  );
};

export default AddonsFinalPrices;
